<template>
  <div class="node-pools-table-body-row" aid="scheduling-tab-content">
    <!--  NODE POOL NAME  -->
    <node-pool-name
      :class="nodePoolClass"
      :is-only-default-node-pool="options.isOnlyDefaultNodePool"
      :node-pool-name="getNodePoolNameByResource(model.resource)"
    />

    <!-- ENTITY  PRIORITY  -->
    <entity-priority-select
      v-if="options.isClusterSupportsPriority"
      :priority="model.resource.priority"
      @update-priority="updatePriority($event.value)"
    />

    <!--  OVER QUOTA  -->
    <runai-select
      dense
      outlined
      class="over-quota-select runai-slim-select"
      :data-testid="`${overQuotaSelector}-${getNodePoolNameByResource(model.resource)}`"
      :model-value="getSelectedOverQuotaOption()"
      @update:model-value="updateNodePoolResourceOverQuota($event.value)"
      :options="overQuotaOptions"
    />

    <!--  LIMIT  -->
    <limit-input
      :model="model"
      :model-value="model.resource.gpu.limit"
      :read-only="options.readOnly"
      :is-alert-on="isLimitAlertOn"
      @update:model-value="updateNodePoolResource(EResourceType.GPU_LIMIT, $event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { RunaiSelect } from "@/components/common/runai-select";

import { NodePoolName } from "@/components/org-unit/quota-management/node-pools-table/node-pool-row/noed-pool-name";

import {
  defaultQuotaOption,
  defaultQuotaPriorityOption,
  EQuotaEntity,
  EResourceType,
  overQuotaOptions,
  overQuotaPriorityOptions,
} from "@/models/resource.model";
import { resourceUtil } from "@/utils/resource.util";
import type { ISelectOption } from "@/models/global.model";
import type { Resources } from "@/swagger-models/org-unit-service-client";
import { orgUnitUtil } from "@/utils/org-unit.util";
import { EntityPrioritySelect } from "@/components/org-unit/quota-management/node-pools-table/node-pool-row/scheduling-tab-content/entity-priority-select";
import type { NodePoolModel, NodePoolOptions } from "@/models/org-unit.model";
import { LimitInput } from "@/components/org-unit/quota-management/quota-inputs/limit-input";

enum ENodePoolRowEmitName {
  UpdateNodePoolPriority = "update-node-pool-priority",
  UpdateEntityPriority = "update-priority",
  UpdateNodePoolResource = "update-node-pool-resource",
  UpdateNodePoolOverQuota = "update-node-pool-over-quota",
}

export default defineComponent({
  name: "scheduling-tab-content",
  components: { LimitInput, EntityPrioritySelect, NodePoolName, RunaiSelect },
  emits: {
    [ENodePoolRowEmitName.UpdateNodePoolPriority]: (payload: { value: string | number; name: string }) => payload,
    [ENodePoolRowEmitName.UpdateNodePoolResource]: (payload: {
      name: string;
      resourceType: EResourceType;
      resourceValue: number | null;
    }) => payload,
    [ENodePoolRowEmitName.UpdateNodePoolOverQuota]: (payload: { name: string; overQuota: number }) => payload,
    [ENodePoolRowEmitName.UpdateEntityPriority]: (payload: { name: string; priority: string }) => payload,
  },
  inject: ["entity"],
  props: {
    options: {
      type: Object as PropType<NodePoolOptions>,
      required: true,
    },
    model: {
      type: Object as PropType<NodePoolModel>,
      required: true,
    },
  },
  computed: {
    isProjectEntity(): boolean {
      return this.entity === EQuotaEntity.project;
    },
    EQuotaEntity(): typeof EQuotaEntity {
      return EQuotaEntity;
    },
    overQuotaOptions(): ISelectOption[] {
      return this.options.isOverQuotaPriorityEnabled ? overQuotaPriorityOptions : overQuotaOptions;
    },
    overQuotaSelector(): string {
      return this.options.isOverQuotaPriorityEnabled ? "over-quota-priority-select" : "over-quota-select";
    },
    EResourceType(): typeof EResourceType {
      return EResourceType;
    },
    ENodePoolRowEmitName(): typeof ENodePoolRowEmitName {
      return ENodePoolRowEmitName;
    },
    isOverQuotaEnabled(): boolean {
      return orgUnitUtil.isResourceOverQuotaEnabled(this.model.resource, this.options.isOverQuotaPriorityEnabled);
    },
    isLimitAlertOn(): boolean {
      return !orgUnitUtil.isOverQuotaValueValid(
        this.model.resource.gpu?.limit,
        this.model.resource.gpu,
        this.isOverQuotaEnabled,
      );
    },
    nodePoolClass(): string {
      if (this.isProjectEntity) return "projects-np-width";
      if (this.options.isOnlyDefaultNodePool) return "departments-np-width-single";
      return "departments-np-width-multiple";
    },
  },
  methods: {
    updateNodePoolResource(resourceType: EResourceType, resourceValue: number | null): void {
      this.$emit(ENodePoolRowEmitName.UpdateNodePoolResource, {
        name: this.model.resource?.nodePool?.name || "",
        resourceType,
        resourceValue,
      });
    },
    updateNodePoolResourceOverQuota(overQuota: number): void {
      this.$emit(ENodePoolRowEmitName.UpdateNodePoolOverQuota, {
        name: this.model.resource?.nodePool?.name || "",
        overQuota,
      });
    },
    updatePriority(priority: string): void {
      this.$emit(ENodePoolRowEmitName.UpdateEntityPriority, {
        name: this.model.resource?.nodePool?.name || "",
        priority,
      });
    },
    getSelectedOverQuotaOption(): ISelectOption {
      if (this.options.isOverQuotaPriorityEnabled) {
        if (this.model.resource.gpu.overQuotaWeight === null || this.model.resource.gpu.overQuotaWeight === undefined) {
          return defaultQuotaPriorityOption;
        }
        return {
          label: resourceUtil.getOverQuotaPriorityKeyByValue(this.model.resource.gpu.overQuotaWeight),
          value: this.model.resource.gpu.overQuotaWeight,
        };
      } else {
        if (this.model.resource.gpu.overQuotaWeight === null || this.model.resource.gpu.overQuotaWeight === undefined) {
          return defaultQuotaOption;
        }
        return {
          label: resourceUtil.getOverQuotaKeyByValue(this.model.resource.gpu.overQuotaWeight),
          value: this.model.resource.gpu.overQuotaWeight,
        };
      }
    },
    getNodePoolNameByResource(resources: Resources): string {
      return orgUnitUtil.getNodePoolNameByResource(resources);
    },
  },
});
</script>
<style lang="scss">
.node-pools-table-body-row {
  .q-field--outlined .q-field__control:before {
    border: none;
  }
}
</style>
<style scoped lang="scss">
.node-pools-table-body-row {
  gap: 8px;
  display: flex;
  align-items: center;
  width: 100%;
  .departments-np-width-single {
    max-width: 255px;
  }
  .departments-np-width-multiple {
    max-width: 223px;
  }
  .projects-np-width {
    width: 100%;
  }
}
.node-pools-table-body-row > *:first-child {
  flex-grow: 1;
}
.node-pools-table-body-row > *:nth-child(n + 2) {
  flex-shrink: 0;
}
.over-quota-select {
  cursor: pointer;
  width: 111px;
  height: 40px;
  border-radius: 5px;
  border: 1px solid $black-30;
  text-align: center;
  color: $black-70;
  background-color: white;
  font-weight: 500;
  transition: box-shadow 0.3s;

  &:hover {
    border: 1px solid $black-70;
  }
  &.w-lg {
    width: 135px;
  }
}
</style>
